import { useState, useRef, FC } from 'react';
import ArrowForwards from '~/components/icons/ArrowForwards';
import { Typography } from '~/components/typography/Typography';
import styled from 'styled-components';
import { colors } from '~/theme/colors';
import { Flex } from '~/components/wrappers/Flex';
import { fontFamilies } from '~/theme/typography';
import { Box } from '~/components/wrappers/Box';
import { SetterOrUpdater } from 'recoil';
import { AppState } from '~/recoil/useAppSSR';
interface ColorProps {
  submitError: boolean;
  isFocused: boolean;
}

const getColor = ({ submitError, isFocused }: ColorProps) => {
  if (submitError) return colors.error;

  return isFocused ? colors.textActive : colors.text;
};

const getHoverColor = ({ submitError }: ColorProps) => {
  if (submitError) return colors.error;

  return colors.textHover;
};

const PasswordForm: FC<{
  setIsAccessible: SetterOrUpdater<AppState>;
}> = ({ setIsAccessible }) => {
  const [password, setPassword] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const passwordInput = useRef<HTMLInputElement>(null);

  const isValidPassword = Boolean(
    password.length && password.trim() === String(window.ENV.PASSWORD).trim(),
  );

  const hasValue = Boolean(password.length);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isValidPassword) {
      setSubmitError(true);

      return;
    }

    setIsAccessible({ isAccessible: true });
  };

  return (
    <Container>
      <FormWrapper
        onSubmit={handleSubmit}
        submitError={submitError}
        isFocused={isFocused}
      >
        <InputWrapper $direction="column">
          <Flex>
            <Input
              value={password}
              type="password"
              onChange={(e) => {
                setPassword(e.target.value);
                setSubmitError(false);
              }}
              placeholder="Enter password"
              aria-label="Enter password to access shop"
              submitError={submitError}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              ref={passwordInput}
            />
            <Button
              type="submit"
              isFocused={isFocused}
              isValidPassword={isValidPassword}
              submitError={submitError}
              hasValue={hasValue}
            >
              <ArrowForwards width="24" height="24" />
            </Button>
          </Flex>
          {submitError && (
            <ErrorMessage variant="p">Incorrect password</ErrorMessage>
          )}
        </InputWrapper>
      </FormWrapper>
    </Container>
  );
};

export default PasswordForm;

const Button = styled.button<{
  isFocused: boolean;
  isValidPassword: boolean;
  submitError: boolean;
  hasValue: boolean;
}>`
  display: ${({ hasValue }) => (hasValue ? 'flex' : 'none')};
  visibility: ${({ hasValue }) => (hasValue ? 'visible' : 'hidden')};
  align-items: center;
  cursor: ${({ isValidPassword }) => (isValidPassword ? 'pointer' : 'default')};
  border: none;
  background-color: unset;

  > svg {
    fill: ${getColor};
  }
`;

const ErrorMessage = styled(Typography)`
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.3rem;
  color: ${({ theme }) => theme.colors.error};
  padding-top: 0.375rem;
  text-align: center;
`;

const Container = styled(Box)`
  margin: 0 auto;
  width: 100%;
  max-width: 70rem;
  padding-top: 7.5rem;
  min-height: calc(100vh - 10.5rem);
  position: relative;

  @media ${({ theme }) => theme.breakpoints.mid} {
    margin-left: 16rem;
    margin-right: 1.5rem;
  }
  @media ${({ theme }) => theme.breakpoints.md} {
    padding: 7.5rem 1.5rem 0;
    margin: 0 auto;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 6.25rem 1rem 0;
  }
`;

const InputWrapper = styled(Flex)`
  position: absolute;
  top: calc(50% + 5.4375rem);
  left: calc(50% - 6.3125rem);

  @media ${({ theme }) => theme.breakpoints.sm} {
    position: unset;
    margin-top: 8.75rem;
  }
`;

const Input = styled.input<{ submitError: boolean }>`
  -webkit-user-select: text;
  border: none;
  width: 100%;
  font-weight: 300;
  font-size: 0.875rem;
  border-radius: 0;
  font-family: ${fontFamilies.CircularStd};
  line-height: 1.625rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme, submitError }) =>
    submitError ? theme.colors.error : theme.colors.text};
  text-align: center;

  ::placeholder {
    text-transform: uppercase;
    font-size: 0.875rem;
    font-weight: 300;
    font-family: ${fontFamilies.CircularStd};
    color: ${({ theme }) => theme.colors.text};
  }

  :focus::-webkit-input-placeholder {
    color: ${({ theme }) => theme.colors.grey500};
  }

  :focus {
    outline: none;
    color: ${({ theme, submitError }) =>
      submitError ? theme.colors.error : theme.colors.textActive};

    ::placeholder {
      color: transparent;
    }
  }
`;

const FormWrapper = styled.form<{ submitError: boolean; isFocused: boolean }>`
  &:hover {
    ${Input} {
      color: ${getHoverColor};

      ::placeholder {
        color: ${getHoverColor};
      }
    }

    ${Button} {
      > svg {
        fill: ${getHoverColor};
      }
    }
  }
`;
